import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, Star, X, GripVertical, CreditCard, Check } from "lucide-react";

const PERIODS = [
  { value: "mensual", label: "Mensual" },
  { value: "anual", label: "Anual" },
  { value: "unico", label: "Pago único" },
];
const CURRENCIES = ["$", "AR$", "US$", "€"];

export default function AdminMembresiasTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [perks, setPerks] = useState([]);
  const [newPerk, setNewPerk] = useState("");
  const [saving, setSaving] = useState(false);

  const field = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30";

  const load = useCallback(async () => {
    const { data } = await api.get("/memberships/all");
    setItems(data);
    return data;
  }, []);
  useEffect(() => { load(); }, [load]);

  const select = (m) => { setSelected(m); setPerks(m.perks || []); setNewPerk(""); };

  const create = async () => {
    try {
      const { data } = await api.post("/memberships", { name: "Nueva categoría", order: items.length });
      toast.success("Categoría creada");
      await load();
      select(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo crear"); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.target);
    try {
      const { data } = await api.patch(`/memberships/${selected.id}`, {
        name: fd.get("name"), color: fd.get("color"), currency: fd.get("currency"),
        price: fd.get("price"), period: fd.get("period"), description: fd.get("description"),
        perks, highlight: fd.get("highlight") === "on", active: fd.get("active") === "on",
        order: parseInt(fd.get("order"), 10) || 0,
      });
      setSelected(data); setPerks(data.perks || []);
      toast.success("Categoría guardada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría de socio?")) return;
    await api.delete(`/memberships/${id}`);
    if (selected?.id === id) setSelected(null);
    toast.success("Categoría eliminada");
    load();
  };

  const addPerk = () => { const v = newPerk.trim(); if (!v) return; setPerks((p) => [...p, v]); setNewPerk(""); };
  const removePerk = (i) => setPerks((p) => p.filter((_, idx) => idx !== i));

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6">
      {/* List */}
      <section className="lg:col-span-1">
        <button onClick={create} data-testid="membership-create-btn" className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-3 rounded-xl hover:bg-[#16235a]">
          <Plus className="h-4 w-4" /> Nueva categoría
        </button>
        <div className="mt-4 space-y-2">
          {items.map((m) => (
            <button
              key={m.id}
              data-testid={`membership-item-${m.name.toLowerCase()}`}
              onClick={() => select(m)}
              className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${selected?.id === m.id ? "bg-navy text-white border-navy" : "bg-white border-gray-100 hover:border-navy/30"}`}
            >
              <span className="h-8 w-8 rounded-full border-2 border-white shadow shrink-0" style={{ backgroundColor: m.color }} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate flex items-center gap-1.5">{m.name} {m.highlight && <Star className="h-3.5 w-3.5 fill-gold text-gold" />}</div>
                <div className={`text-xs ${selected?.id === m.id ? "text-gold" : "text-gray-400"}`}>
                  {m.currency} {m.price || "—"} · {m.period}{m.active === false ? " · oculta" : ""}
                </div>
              </div>
            </button>
          ))}
          {items.length === 0 && <p className="text-sm text-gray-400 px-1">Sin categorías todavía.</p>}
        </div>
      </section>

      {/* Editor */}
      <section className="lg:col-span-2">
        {!selected ? (
          <div className="h-full min-h-[300px] bg-white rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400">
            <CreditCard className="h-10 w-10" />
            <p className="mt-3">Seleccioná o creá una categoría de socio</p>
          </div>
        ) : (
          <form onSubmit={save} key={selected.id} data-testid="membership-editor" className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-navy font-bold"><CreditCard className="h-5 w-5 text-gold" /> {selected.name}</div>
              <button type="button" onClick={() => remove(selected.id)} data-testid="membership-delete-btn" className="inline-flex items-center gap-1.5 text-[#c0392b] border border-[#c0392b]/30 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#c0392b]/5">
                <Trash2 className="h-3.5 w-3.5" /> Eliminar
              </button>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1">Nombre</span>
                <input name="name" defaultValue={selected.name} className={field} data-testid="membership-name" />
              </label>
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1">Color</span>
                <div className="flex items-center gap-2">
                  <input type="color" name="color" defaultValue={selected.color || "#d4a017"} className="h-9 w-12 rounded border border-gray-200 cursor-pointer" data-testid="membership-color" />
                  <span className="text-xs text-gray-400">Se usa en la tarjeta</span>
                </div>
              </label>
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1">Moneda</span>
                <select name="currency" defaultValue={selected.currency || "$"} className={`bg-white ${field}`} data-testid="membership-currency">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1">Costo</span>
                <input name="price" defaultValue={selected.price} placeholder="Ej: 9.500" className={field} data-testid="membership-price" />
              </label>
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1">Período</span>
                <select name="period" defaultValue={selected.period || "mensual"} className={`bg-white ${field}`} data-testid="membership-period">
                  {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1">Orden</span>
                <input type="number" name="order" defaultValue={selected.order || 0} className={field} data-testid="membership-order" />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="block text-navy font-semibold mb-1">Descripción</span>
                <textarea name="description" defaultValue={selected.description || ""} rows={2} className={`resize-none ${field}`} data-testid="membership-description" />
              </label>
            </div>

            {/* Perks / items del club */}
            <div className="mt-4">
              <span className="block text-navy font-semibold text-sm mb-2">Beneficios / items que ofrece</span>
              <div className="space-y-2">
                {perks.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#eef1f7] rounded-lg px-3 py-2" data-testid={`membership-perk-${i}`}>
                    <Check className="h-4 w-4 text-navy shrink-0" />
                    <span className="flex-1 text-sm text-navy">{p}</span>
                    <button type="button" onClick={() => removePerk(i)} className="h-6 w-6 rounded flex items-center justify-center text-gray-400 hover:text-[#c0392b] hover:bg-white"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                {perks.length === 0 && <p className="text-xs text-gray-400">Agregá los beneficios de esta categoría.</p>}
              </div>
              <div className="mt-2 flex gap-2">
                <input
                  value={newPerk}
                  onChange={(e) => setNewPerk(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPerk(); } }}
                  placeholder="Nuevo beneficio (ej: Descuentos en la tienda)"
                  className={field}
                  data-testid="membership-new-perk"
                />
                <button type="button" onClick={addPerk} data-testid="membership-add-perk" className="inline-flex items-center gap-1 bg-navy text-white font-semibold px-4 rounded-lg hover:bg-[#16235a] shrink-0">
                  <Plus className="h-4 w-4" /> Agregar
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-5">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
                <input type="checkbox" name="highlight" defaultChecked={selected.highlight} data-testid="membership-highlight" className="h-4 w-4 accent-navy" /> Destacar como "más elegida"
              </label>
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
                <input type="checkbox" name="active" defaultChecked={selected.active !== false} data-testid="membership-active" className="h-4 w-4 accent-navy" /> Visible en el sitio
              </label>
            </div>

            <button data-testid="membership-save-btn" disabled={saving} className="mt-5 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar categoría
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
